const { arrayToHashmap, getDecimalsFromPow } = require('../../utils/helpers');
const { toUpperCaseFirstLetter } = require('../../utils/String');
const config = require('../config.js');
const mimGauge = require('./json/mim/gauge');
const pools = require('../pools');
const allCoins = require('../coins');
const {
  poolsData,
  factoryPoolDataSkeleton,
  ...restChainABIsExports
} = require(`./abis-${config.blockchainId}.js`);
const sharedABIs = require('./abis-shared.js');
const { default: getFactoryPools } = require('../../utils/data/getFactoryPools');
const { default: getFactoryV2Pools } = require('../../utils/data/getFactoryV2Pools');
const { default: getFactoryCryptoPools } = require('../../utils/data/getFactoryCryptoPools');
const { default: getFactoryV2Gauges } = require('../../utils/data/getFactoryV2Gauges');

const formatFileName = (poolId) => toUpperCaseFirstLetter(poolId.replace(/-/g, ''));

const { poolIds, poolIdsWithAliases } = pools;

const poolsDataIds = Array.from(Object.keys(poolsData));
const poolsWithoutABIData = poolIds.filter((id) => !poolsDataIds.includes(id));

if (poolsWithoutABIData.length) {
  throw new Error(`All pools need to have a corresponding data object under \`constants/abis/\`. Pools not found: ${poolsWithoutABIData.join(', ')}`);
}

const abiData = arrayToHashmap(poolIdsWithAliases.map((id) => {
  const pool = pools.find((pool) => pool.id === id || pool.idAlias === id);
  return [id, poolsData[pool.id]];
}));

let areMainPoolsAugmented = false;
let factoryPoolsLoaded = false;
let factoryPoolsV2Loaded = false;
let factoryPoolsCryptoLoaded = false;

setTimeout(() => {
  if (config.factoryRegistryAddress) {
    getFactoryPools().then((factoryPools) => {
      factoryPools.forEach(({
        id,
        address,
        coins,
        coinsAddresses,
        underlyingCoinsAddresses,
        underlyingDecimals,
      }) => {
        const key = `factory-${id}`;
        const lpToken = coins[coins.length - 1];
        const basePool = (
          lpToken.symbol === '3Crv' ? '3pool' :
            lpToken.symbol === 'sbtcCrv' ? 'sbtc' :
              undefined
        );

        const coinsPrecisions = coins.map(({ decimals }) => 10 ** decimals);
        const underlyingCoinsPrecisions = underlyingDecimals.map((decimals) => 10 ** decimals);

        abiData[key] = {
          ...factoryPoolDataSkeleton,
          N_COINS: coins.length,
          coin_precisions: coinsPrecisions,
          wrapped_precisions: coinsPrecisions,
          swap_address: address,
          token_address: address,
          underlying_coins: coins.map(({ address }) => address),
          coins: coinsAddresses,
          base_pool: basePool,
          meta_N: coins.length + underlyingCoinsAddresses.length - 1,
          meta_precisions: [...coinsPrecisions, ...underlyingCoinsPrecisions.slice(1)],
          meta_coin_precisions: underlyingCoinsPrecisions,
          meta_wrapped_precisions: coinsPrecisions,
          meta_coins: underlyingCoinsAddresses.slice(1),
          all_coins: [...coinsAddresses, ...underlyingCoinsAddresses.slice(1)],
        }
      });

      factoryPoolsLoaded = true;
    });
  } else {
    factoryPoolsLoaded = true;
  }

  if (config.factoryV2RegistryAddress) {
    getFactoryV2Pools().then((factoryV2Pools) => {
      /**
       * Allows to retrieve either main pools or facto pools data without factory data being
       * fully initialized yet; useful when a facto pool is a meta pool using another facto
       * pool as a base pool (i.e. when facto pools haven't been added to the main pools object
       * yet, but we do need access to that base pool's data).
       * Returns these properties only for facto pools: id name coins underlyingCoins
       */
      const safeGetPoolById = (poolId) => {
        if (!poolId) return null;

        const isFactoV2Pool = poolId.startsWith('factory-v2');
        if (!isFactoV2Pool) return pools.getById(poolId);

        const poolData = factoryV2Pools.find(({ id }) => id === poolId);
        const transformToMainPoolDecimalsFormat = (coins) => (
          coins?.map(({ decimals, ...coin }) => ({
            ...coin,
            decimals: (10 ** decimals),
          }))
        );
        return new Proxy({
          id: poolId,
          name: poolData.name,
          coins: transformToMainPoolDecimalsFormat(poolData.coins),
          underlyingCoins: transformToMainPoolDecimalsFormat(poolData.underlyingCoins),
        }, {
          get: function(target, name) {
            if (name in target) return target[name];

            console.log('trying to retrieve non-hardcoded data from abi simulation', target, name, poolId)
            return abiData[poolId][name];
          },
        });
      };

      const factoryV2ImplementationAbiMap = new Map([
        ['plain2basic', restChainABIsExports.Plain2Basic],
        ['plain2basicema', restChainABIsExports.Plain2BasicEMA],
        ['plain2balances', restChainABIsExports.Plain2Balances],
        ['plain2eth', restChainABIsExports.Plain2ETH],
        ['plain2ethema', restChainABIsExports.Plain2ETHEMA],
        ['plain2optimized', restChainABIsExports.Plain2Optimized],
        ['plain3basic', restChainABIsExports.Plain3Basic],
        ['plain3balances', restChainABIsExports.Plain3Balances],
        ['plain3eth', restChainABIsExports.Plain3ETH],
        ['plain3optimized', restChainABIsExports.Plain3Optimized],
        ['plain4basic', restChainABIsExports.Plain4Basic],
        ['plain4balances', restChainABIsExports.Plain4Balances],
        ['plain4eth', restChainABIsExports.Plain4ETH],
        ['plain4optimized', restChainABIsExports.Plain4Optimized],
        ['v1metausd', restChainABIsExports.V1MetaUSD],
        ['metausd', restChainABIsExports.MetaUSD],
        ['metausdbalances', restChainABIsExports.MetaUSDBalances],
        ['v1metabtc', restChainABIsExports.V1MetaBTC],
        ['metabtc', restChainABIsExports.MetaBTC],
        ['metabtcbalances', restChainABIsExports.MetaBTCBalances],
        ...(config.factoryV2MetaBtc2BasePoolId ? [
          [`metabtc-${config.factoryV2MetaBtc2BasePoolId}`, restChainABIsExports[`MetaBTC${formatFileName(config.factoryV2MetaBtc2BasePoolId)}`]],
          [`metabtcbalances-${config.factoryV2MetaBtc2BasePoolId}`, restChainABIsExports[`MetaBTCBalances${formatFileName(config.factoryV2MetaBtc2BasePoolId)}`]],
        ] : []),
        ...(config.factoryV2MetaBtc3BasePoolId ? [
          [`metabtc-${config.factoryV2MetaBtc3BasePoolId}`, restChainABIsExports[`MetaBTC${formatFileName(config.factoryV2MetaBtc3BasePoolId)}`]],
          [`metabtcbalances-${config.factoryV2MetaBtc3BasePoolId}`, restChainABIsExports[`MetaBTCBalances${formatFileName(config.factoryV2MetaBtc3BasePoolId)}`]],
        ] : []),
        ...(config.factoryV2MetaUsd2BasePoolId ? [
          [`metausd-${config.factoryV2MetaUsd2BasePoolId}`, restChainABIsExports[`MetaUSD${formatFileName(config.factoryV2MetaUsd2BasePoolId)}`]],
          [`metausdbalances-${config.factoryV2MetaUsd2BasePoolId}`, restChainABIsExports[`MetaUSDBalances${formatFileName(config.factoryV2MetaUsd2BasePoolId)}`]],
        ] : []),
      ]);

      factoryV2Pools.forEach(({
        id,
        address,
        coins,
        coinsAddresses,
        // underlyingCoinsAddresses,
        // underlyingDecimals,
        implementation,
        gauge,
        gaugeAddress,
      }) => {
        const key = id;

        const isMetaPool = (
          implementation === 'v1metausd' ||
          implementation === 'metausd' ||
          implementation === 'metausdbalances' ||
          implementation === 'v1metabtc' ||
          implementation.startsWith('metabtc') ||
          implementation.startsWith('metabtcbalances') ||
          implementation.startsWith('metausd') ||
          implementation.startsWith('metausdbalances')
        );

        const isUsdMetaPool = isMetaPool && (implementation.startsWith('metausd') || implementation.startsWith('v1metausd'));
        const isBtcMetaPool = isMetaPool && (implementation.startsWith('metabtc') || implementation.startsWith('v1metabtc'));

        const metaUsdBasePool = (
          (config.factoryV2MetaUsd2BasePoolId && implementation.endsWith(`-${config.factoryV2MetaUsd2BasePoolId}`)) ?
            safeGetPoolById(config.factoryV2MetaUsd2BasePoolId) :
            safeGetPoolById(config.factoryV2MetaUsdBasePoolId)
        );

        const metaBtcBasePool = (
          (config.factoryV2MetaBtc2BasePoolId && implementation.endsWith(`-${config.factoryV2MetaBtc2BasePoolId}`)) ?
            safeGetPoolById(config.factoryV2MetaBtc2BasePoolId) :
            (config.factoryV2MetaBtc3BasePoolId && implementation.endsWith(`-${config.factoryV2MetaBtc3BasePoolId}`)) ?
              safeGetPoolById(config.factoryV2MetaBtc3BasePoolId) :
              safeGetPoolById(config.factoryV2MetaBtcBasePoolId)
        );

        const basePool = (
          isUsdMetaPool ? metaUsdBasePool :
            isBtcMetaPool ? metaBtcBasePool :
              undefined
        );

        const basePoolId = basePool?.id || undefined;

        const depositZapAddress = (
          (isUsdMetaPool && metaUsdBasePool.id === config.factoryV2MetaUsd2BasePoolId) ? config.factoryV2MetaUsd2DepositZapAddress :
            (isBtcMetaPool && metaBtcBasePool.id === config.factoryV2MetaBtc3BasePoolId) ? config.factoryV2MetaBtc3DepositZapAddress :
              isUsdMetaPool ? config.factoryV2MetaUsdDepositZapAddress :
                config.factoryV2MetaBtcDepositZapAddress
        );

        const isMetaPoolAndTwoLevelsDeep = (
          isMetaPool &&
          basePool &&
          basePool.underlyingCoins
        );

        const underlyingCoinsAddresses = (
          (isMetaPool && isMetaPoolAndTwoLevelsDeep) ? [
            coinsAddresses[0],
            ...basePool.underlyingCoins.map(({ address }) => address),
          ] :
            (isMetaPool && !isMetaPoolAndTwoLevelsDeep) ? [
              coinsAddresses[0],
              ...basePool.coins.map(({ address }) => address),
            ] :
              coinsAddresses
        );

        const underlyingDecimals = (
          (isMetaPool && isMetaPoolAndTwoLevelsDeep) ? [
            coins[0].decimals,
            ...basePool.underlyingCoins.map(({ decimals }) => getDecimalsFromPow(decimals)),
          ] :
            (isMetaPool && !isMetaPoolAndTwoLevelsDeep) ? [
              coins[0].decimals,
              ...basePool.coins.map(({ decimals }) => getDecimalsFromPow(decimals)),
            ] :
              coins.map(({ decimals }) => decimals)
        );

        const coinsPrecisions = coins.map(({ decimals }) => 10 ** decimals);
        const underlyingCoinsPrecisions = underlyingDecimals.map((decimals) => 10 ** decimals);

        abiData[key] = {
          ...factoryPoolDataSkeleton,
          N_COINS: coins.length,
          coin_precisions: coinsPrecisions,
          wrapped_precisions: coinsPrecisions,
          swap_address: address,
          token_address: address,
          gauge_address: gauge || gaugeAddress || factoryPoolDataSkeleton.gauge_address,
          underlying_coins: coins.map(({ address }) => address),
          coins: coinsAddresses,
          base_pool: basePoolId,
          is_meta: isMetaPool,
          meta_N: isMetaPool ? coins.length + underlyingCoinsAddresses.length - 1 : undefined,
          meta_precisions: isMetaPool ? [...coinsPrecisions, ...underlyingCoinsPrecisions.slice(1)] : undefined,
          meta_coin_precisions: isMetaPool ? underlyingCoinsPrecisions : undefined,
          meta_wrapped_precisions: isMetaPool ? coinsPrecisions : undefined,
          meta_coins: isMetaPool ? underlyingCoinsAddresses.slice(1) : undefined,
          all_coins: isMetaPool ? [...coinsAddresses, ...underlyingCoinsAddresses.slice(1)] : undefined,
          deposit_abi: (
            (isMetaPool && config.factoryV2MetaUsd2DepositZapAbi && isUsdMetaPool && metaUsdBasePool.id === config.factoryV2MetaUsd2BasePoolId) ? config.factoryV2MetaUsd2DepositZapAbi :
              (isMetaPool && config.factoryV2MetaBtc3DepositZapAbi && isBtcMetaPool && metaBtcBasePool.id === config.factoryV2MetaBtc3BasePoolId) ? config.factoryV2MetaBtc3DepositZapAbi :
                (isMetaPool && config.factoryV2MetaUsdDepositZapAbi && isUsdMetaPool) ? config.factoryV2MetaUsdDepositZapAbi :
                  (isMetaPool && config.factoryV2MetaBtcDepositZapAbi && isBtcMetaPool) ? config.factoryV2MetaBtcDepositZapAbi :
                    isMetaPool ? factoryPoolDataSkeleton.deposit_abi :
                      undefined
          ),
          deposit_address: isMetaPool ? depositZapAddress : undefined,
          swap_abi: factoryV2ImplementationAbiMap.get(implementation),
          gauge_abi: mimGauge,
          tethered: new Array(coins.length).fill(false),
          use_lending: new Array(coins.length).fill(false),
          is_plain: new Array(coins.length).fill(true),
          has_eth: (implementation === 'plain2eth' || implementation === 'plain2ethema' || implementation === 'plain3eth' || implementation === 'plain4eth'),
        };

        // Override for a facto pool that had a custom gauge, now retired
        if (config.blockchainId === 'avalanche' && key === 'factory-v2-30') {
          abiData[key].old_gauge = '0xA4106e1206d313a86051c6B5bc475181a81Dbc0f';
        }
      });

      factoryPoolsV2Loaded = true;
    });

    if (config.blockchainId !== 'ethereum') {
      getFactoryV2Gauges().then((factoryV2Gauges) => {
        // Augment main pools abi data on sidechains: if a facto gauge was deployed for a main
        // pool, update its gauge address with the newest one
        Array.from(Object.entries(abiData)).forEach(([key, poolAbiData]) => {
          const matchingFactoGauge = factoryV2Gauges.find(({ swap_token }) => (
            swap_token.toLowerCase() === poolAbiData.token_address.toLowerCase()
          ));
          if (matchingFactoGauge && matchingFactoGauge.gauge.toLowerCase() !== poolAbiData?.gauge_address?.toLowerCase()) {
            abiData[key] = {
              ...abiData[key],
              gauge_address: matchingFactoGauge.gauge,
              gauge_abi: mimGauge,
              old_gauge: poolAbiData.gauge_address || undefined,
            };

            // If this pool not only has a fresher gauge replacing a previous hardcoded one, but ALSO
            // had an old gauge already hardcoded (bringing the total of gauges for that pool from 2 to 3),
            // then also instantiate this second old gauge
            if (poolAbiData?.old_gauge) {
              abiData[key] = {
                ...abiData[key],
                old_gauge_2: poolAbiData.old_gauge || undefined,
              };
            }
          }
        });

        areMainPoolsAugmented = true;
      });
    } else {
      areMainPoolsAugmented = true;
    }
  } else {
    factoryPoolsV2Loaded = true;
    areMainPoolsAugmented = true;
  }

  if (config.factoryCryptoRegistryAddress) {
    getFactoryCryptoPools().then((factoryCryptoPools) => {
      factoryCryptoPools.forEach(({
        id,
        address,
        lpTokenAddress,
        coins,
        implementation,
        coinsAddresses,
        gauge,
        gaugeAddress,
      }) => {
        const key = id;
        const isMetaPool = implementation.startsWith('metacrypto');

        const metaCryptoBasePool = (
          (config.factoryCryptoMetaBasePoolId && implementation === 'metacrypto') ?
            pools.getById(config.factoryCryptoMetaBasePoolId) :
            (config.factoryCryptoMeta2BasePoolId && implementation === `metacrypto-${config.factoryCryptoMeta2BasePoolId}`) ?
              pools.getById(config.factoryCryptoMeta2BasePoolId) :
              undefined
        );

        const basePool = (
          isMetaPool ? metaCryptoBasePool :
            undefined
        );

        const basePoolId = basePool?.name || undefined;

        const depositZapAddress = (
          (isMetaPool && implementation === 'metacrypto') ? config.factoryCryptoMetaDepositZapAddress :
            (isMetaPool && implementation === `metacrypto-${config.factoryCryptoMeta2BasePoolId}`) ? config.factoryCryptoMeta2DepositZapAddress :
              null
        );

        const underlyingCoinsAddresses = (
          isMetaPool ? [
            coinsAddresses[0],
            ...basePool.coins.map(({ address }) => address),
          ] :
            coinsAddresses
        );

        const underlyingDecimals = (
          isMetaPool ? [
            coins[0].decimals,
            ...basePool.coins.map(({ decimals }) => getDecimalsFromPow(decimals)),
          ] :
            coins.map(({ decimals }) => decimals)
        );

        const coinsPrecisions = coins.map(({ decimals }) => 10 ** decimals);
        const underlyingCoinsPrecisions = underlyingDecimals.map((decimals) => 10 ** decimals);

        abiData[key] = {
          ...factoryPoolDataSkeleton,
          N_COINS: coins.length,
          coin_precisions: coinsPrecisions,
          wrapped_precisions: coinsPrecisions,
          swap_address: address,
          token_address: lpTokenAddress,
          gauge_address: gauge || gaugeAddress || factoryPoolDataSkeleton.gauge_address,
          underlying_coins: coins.map(({ address }) => address),
          coins: coinsAddresses,
          base_pool: basePoolId,
          is_meta: isMetaPool,
          meta_N: isMetaPool ? coins.length + underlyingCoinsAddresses.length - 1 : undefined,
          meta_precisions: isMetaPool ? [...coinsPrecisions, ...underlyingCoinsPrecisions.slice(1)] : undefined,
          meta_coin_precisions: isMetaPool ? underlyingCoinsPrecisions : undefined,
          meta_wrapped_precisions: isMetaPool ? coinsPrecisions : undefined,
          meta_coins: isMetaPool ? underlyingCoinsAddresses.slice(1) : undefined,
          all_coins: isMetaPool ? [...coinsAddresses, ...underlyingCoinsAddresses.slice(1)] : undefined,
          deposit_abi: (
            (isMetaPool && implementation === 'metacrypto') ? config.factoryCryptoMetaDepositZapAbi :
              (isMetaPool && implementation === `metacrypto-${config.factoryCryptoMeta2BasePoolId}`) ? config.factoryCryptoMeta2DepositZapAbi :
                undefined
          ),
          deposit_address: isMetaPool ? depositZapAddress : undefined,
          swap_abi: isMetaPool ? sharedABIs.SideFactoCryptoMetaPool : sharedABIs.SideFactoCryptoPool,
          gauge_abi: mimGauge,
          tethered: new Array(coins.length).fill(false),
          use_lending: new Array(coins.length).fill(false),
          is_plain: new Array(coins.length).fill(true),
          has_eth: coins.some(({ address }) => address.toLowerCase() === allCoins[config.nativeAssetErc20WrapperId].address.toLowerCase()),
          pool_type: 'crypto',
        };
      });

      factoryPoolsCryptoLoaded = true;
    });
  } else {
    factoryPoolsCryptoLoaded = true;
  }
}, 0);

const mergedABIs = {
  ...sharedABIs,
  ...restChainABIsExports,
  ...abiData,
  contractAbis: abiData,
  minter_address: (
    (config.blockchainId !== 'ethereum' && config.sidechainFactoryRegistryAddress) ?
      config.sidechainFactoryRegistryAddress :
      sharedABIs.minter_address
  ),
  isFactoryAbiDataLoaded: () => (
    areMainPoolsAugmented &&
    factoryPoolsLoaded &&
    factoryPoolsV2Loaded &&
    factoryPoolsCryptoLoaded
  ),
};

module.exports = mergedABIs;
